"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Eye, FileText, ArrowLeft, Globe, Scale, Key, Server, Users, RefreshCw } from "lucide-react";
import Link from "next/link";

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState<number>(0);

  const sections = [
    {
      icon: <Eye size={22} className="text-emerald-600" />,
      title: "1. What We Collect",
      summary: "What details we collect when you book a job or register an account.",
      content: "When you book a job, we collect your name, email, phone number, service address/postcode, and a description (plus any photos or videos) of the issue. Contractors provide business name, trade, contact details, and service-area postcodes when they register. Some contractor business listings (name, trade, publicly listed phone/email, service area) are sourced from publicly available business directories to help customers find local trades faster - these contractors can verify, edit, or request removal of their listing at any time via our contractor verification page or by contacting us."
    },
    {
      icon: <Lock size={22} className="text-blue-600" />,
      title: "2. How We Use It",
      summary: "How we use collected information to run the marketplace.",
      content: "We use your information to: match your job to relevant local contractors by trade and postcode, generate an AI-estimated price range for budgeting purposes, let contractors submit bids and let you choose one, process payment via Stripe once you confirm the job is complete, and enable in-app chat or masked calling with your assigned contractor. Analytics cookies help us understand site usage; you can decline non-essential cookies at any time via our cookie settings."
    },
    {
      icon: <Globe size={22} className="text-purple-600" />,
      title: "3. Who We Share It With",
      summary: "The limited, specific cases where data is shared with third parties.",
      content: "We never sell your personal information. To coordinate a job, we share the necessary details (name, job description, service address, and contact details once mutually agreed) with the specific contractor assigned to that job. Payment details are shared directly with Stripe, our PCI-compliant payment processor - we do not store your card details ourselves."
    },
    {
      icon: <Server size={22} className="text-rose-600" />,
      title: "4. Security",
      summary: "How we protect the data we hold.",
      content: "Passwords are hashed with bcrypt before storage. Data is transmitted over HTTPS/TLS. Our database is hosted on managed PostgreSQL infrastructure (Neon) with access-controlled credentials, and uploaded photos/videos are stored on Amazon S3. We take reasonable steps to protect your data but no online system can be guaranteed 100% secure."
    },
    {
      icon: <Users size={22} className="text-amber-600" />,
      title: "5. Your Rights",
      summary: "Your rights under the Australian Privacy Principles (Privacy Act 1988).",
      content: "As an Australian business, we handle personal information in line with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth). You can request access to the personal information we hold about you, ask us to correct it, or ask us to delete your account and associated data. If your business details were listed from a public directory and you'd like them corrected or removed, contact us and we'll action it promptly. If you have an unresolved privacy concern, you may also contact the Office of the Australian Information Commissioner (OAIC)."
    }
  ];

  return (
    <main className="min-h-screen bg-mesh pb-24 pt-32">
      <div className="mx-auto max-w-5xl px-6">
        
        {/* Hero Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest mb-6 border border-emerald-100">
            <ShieldCheck size={12} />
            Privacy Safeguards Enabled
          </div>
          <h1 className="text-5xl font-black tracking-tight text-slate-950 mb-6 italic">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-500 font-medium max-w-2xl mx-auto">
            At FIXER, we treat your property credentials, location records, and billing data with the same precision and care we apply to our dispatch engineering.
          </p>
        </motion.div>

        {/* Policy Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-16">
          
          {/* Quick-Nav sidebar on the Left */}
          <div className="lg:col-span-4 lg:sticky lg:top-28 space-y-3">
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest px-4 mb-2">Sections</p>
            {sections.map((sec, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSection(idx)}
                className={`w-full p-4 rounded-2xl border text-left flex items-center gap-4 transition-all ${
                  activeSection === idx
                    ? "bg-slate-950 border-slate-950 text-white shadow-lg"
                    : "bg-white/40 border-white/50 text-slate-600 hover:bg-white hover:border-slate-200"
                }`}
              >
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                  activeSection === idx ? "bg-white/10 text-emerald-400" : "bg-white text-slate-700 shadow-sm"
                }`}>
                  {sec.icon}
                </div>
                <div className="overflow-hidden">
                  <p className={`text-xs font-bold truncate ${activeSection === idx ? "text-white" : "text-slate-900"}`}>
                    {sec.title}
                  </p>
                  <p className={`text-[10px] truncate font-medium mt-0.5 ${activeSection === idx ? "text-slate-400" : "text-slate-400"}`}>
                    {sec.summary}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Active section Detail Card on the Right */}
          <div className="lg:col-span-8">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="glass p-10 md:p-12 rounded-[3.5rem] border border-white/60 shadow-xl relative min-h-[400px] flex flex-col justify-between"
            >
              <div>
                <div className="h-16 w-16 rounded-2xl bg-white shadow-inner flex items-center justify-center border border-slate-50 mb-8">
                  {sections[activeSection].icon}
                </div>
                <h2 className="text-3xl font-black text-slate-950 mb-6 italic">
                  {sections[activeSection].title}
                </h2>
                <p className="text-slate-600 font-medium leading-[1.9] italic text-base">
                  {sections[activeSection].content}
                </p>
              </div>

              <div className="mt-12 pt-8 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-400">
                <span className="uppercase tracking-widest">Section {activeSection + 1} of {sections.length}</span>
                <button
                  onClick={() => setActiveSection((prev) => (prev + 1) % sections.length)}
                  className="text-emerald-600 hover:text-emerald-700 flex items-center gap-1 group"
                >
                  Next Section
                  <ArrowLeft size={14} className="rotate-180 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          </div>

        </div>

        {/* Global Compliance callout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-10 md:p-12 rounded-[3rem] bg-slate-950 text-white shadow-2xl relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <Scale size={120} />
          </div>
          <h2 className="text-2xl font-black mb-6 italic">Australian Privacy Compliance</h2>
          <p className="text-slate-400 font-medium leading-relaxed mb-8 max-w-3xl">
            FixerLocal is operated by Automation Partners Pty Ltd and handles personal information in accordance with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth). This policy applies to customers and contractors using our Australian marketplace.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link href="/terms" className="px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-widest transition-all">
              Terms of Service
            </Link>
            <Link href="/cookies" className="px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-widest transition-all">
              Manage Cookies Preference
            </Link>
            <Link href="/contact" className="px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-xs font-black uppercase tracking-widest transition-all">
              Contact Us
            </Link>
          </div>
        </motion.div>

        {/* Back navigation */}
        <div className="mt-16 text-center">
          <Link href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-600 transition-colors text-sm font-bold uppercase tracking-widest">
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>

      </div>
    </main>
  );
}
